import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { setLocalStorageValue, removeLocalStorageValue } from './localstorage'
import { getData, postData, getLocalUrl } from './request'
import { dataFetcher } from './datafetcher'
import { addCases } from './utils'

export const selectedMetricsKey = 'trainer/selectedMetrics'

const ANT_DEVICE_TYPE_FITNESS_EQUIPMENT = 0x11

const pwrName = 'pwr'

export const Modes = {
    none: 'none',
    scanning: 'scanning',
    configurating: 'configurating',
    configured: 'configured',
    error: 'error'
}

export function getError(state) {
    return state && state.error
}

export function isNoData(state) {
    return !state || !state.data ||
        ((!state.data.metrics || !state.data.metrics.length) &&
         (!state.data.equipment || !state.data.equipment.length))
}

function setError(state, { error }) {
    if (!state) return

    // Remember the mode we were in so it can be recovered once live data
    // resumes. A transient network failure (e.g. /scan or a /data poll racing
    // the daemon startup) must not permanently leave the page in "error" mode
    // with the banner stuck over realtime values.
    if (state.mode !== Modes.error)
        state.preErrorMode = state.mode

    state.mode = Modes.error
    state.error = error
}

// Clear any stale failure when a fresh operation starts or succeeds.
function clearError(state) {
    if (!state) return

    state.error = null
    state.preErrorMode = undefined
}

function setData(state, { payload }) {
    if (!state) return

    function getData(sensors, selectedMetrics) {
        function filterSelected(data, selectedMetrics) {
            if (!selectedMetrics || !selectedMetrics.length) return data

            return data.filter(item => !!selectedMetrics.find(metric => item.name === metric.name &&
                item.id === metric.id && item.type === metric.type))
        }

        if (!sensors) return {}

        let rawMetrics = []
        let equipment = []

        sensors.forEach(sensor => {
            const { type, id } = sensor

            if (type !== ANT_DEVICE_TYPE_FITNESS_EQUIPMENT) {
                const { data } = sensor
                Object.getOwnPropertyNames(data).forEach(name => {
                    rawMetrics.push({
                        name,
                        value: data[name],
                        type,
                        id
                    })
                })
            } else {
                equipment.push({ type, id, name: pwrName, data: sensor.data || {} })
            }
        })

        rawMetrics.sort((a, b) => a.name.localeCompare(b.name))

        rawMetrics = filterSelected(rawMetrics, selectedMetrics)

        equipment = filterSelected(equipment, selectedMetrics)

        const metrics = []

        let prevMetric
        rawMetrics.forEach(metric => {
            if (!prevMetric || prevMetric.name !== metric.name) {
                metrics.push({
                    name: metric.name,
                    items: []
                })
            }

            metrics[metrics.length - 1].items.push({
                value: metric.value,
                type: metric.type,
                id: metric.id
            })

            prevMetric = metric
        })


        return { metrics, equipment }
    }

    state.data = getData(payload.sensors, state.selectedMetrics)

    // Live data is proof connectivity is healthy now. If we were stuck in
    // "error" from an earlier transient failure, fall back to the mode we had
    // before it and drop the stale banner instead of hiding realtime values.
    if (state.mode === Modes.error) {
        state.mode = state.preErrorMode || Modes.configured
        state.preErrorMode = undefined
        state.error = null
    }

    dataFetcher.next()
}

function startsScanning(state) {
    if (!state) return

    state.mode = Modes.scanning
    clearError(state)

    state.data = {}
    state.selectedMetrics = []

    dataFetcher.stop()
    removeLocalStorageValue(selectedMetricsKey)
}

function setSensorsScanning(state) {
    if (!state) return

    clearError(state)
    dataFetcher.start()
}

function startConfiguration(state) {
    state.mode = Modes.configurating

    dataFetcher.stop()
}

function setConfigured(state, { payload }) {
    state.mode = Modes.configured
    clearError(state)

    state.selectedMetrics = payload

    dataFetcher.start();

    if (payload.length) {
        setLocalStorageValue(selectedMetricsKey, payload)
    } else {
        removeLocalStorageValue(selectedMetricsKey)
    }
}

function postSensorsData(data) {
    const fullData = [...data]

    const goldPwr = fullData.find(item => item.name === pwrName)
    const trainer = fullData.find(item => item.type === ANT_DEVICE_TYPE_FITNESS_EQUIPMENT)

    if (trainer && goldPwr) {
        trainer.gold = { id: goldPwr.id, type: goldPwr.type }
    }

    return postData(getLocalUrl('configure'), fullData)
        .then(json => {
            let res = -1

            if (json) {
                res = json.result
            }

            if (res !== 0) {
                return Promise.reject({ code: res })
            }

            return data
        })
}

function postPwr(data, api) {
    const state = api.getState()

    const { equipment } = state?.sensors?.data

    if (!Array.isArray(equipment) || !equipment.length) {
        throw new Error(`Не выбрано фитнесс оборудывание`)
    }

    // POST /data expects an array of sensor-write records, each carrying a
    // single `value` key. Build that from the selected equipment and the
    // incoming `{ pwr: <target> }` set-point.
    const payload = equipment.map(eq => ({
        id: eq.id,
        type: eq.type,
        name: 'pwr',
        value: data && data.pwr
    }))

    return postData(getLocalUrl('data'), payload)
        .then(() => 0)
}


export const asyncScanSensors = createAsyncThunk('asyncScanSensors', postData.bind(undefined, getLocalUrl('scan'), null))
export const asyncGetSensorsData = createAsyncThunk('asyncGetSensorsData', getData.bind(undefined, getLocalUrl('data')))
export const asyncConfigureSensors = createAsyncThunk('asyncConfigureSensors', postSensorsData)
export const asyncPostPwr = createAsyncThunk('asyncPostPwr', postPwr)

export const sensorsSlice = createSlice({
    name: 'sensors',
    initialState: {
        mode: Modes.none,
        data: {},
        selectedMetrics: []
    },
    extraReducers: builder => {
        addCases(builder, asyncScanSensors, setSensorsScanning, startsScanning, setError);
        addCases(builder, asyncGetSensorsData, setData, undefined, setError);
        addCases(builder, asyncConfigureSensors, setConfigured, startConfiguration, setError);
    }
})
